import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { NotificationProvider } from './context/NotificationContext';
import { AdminNotificationProvider } from './context/AdminNotificationContext';
import { MessageProvider } from './context/MessageContext';
import { HelmetProvider } from 'react-helmet-async';
import ProtectedRoute from './components/ProtectedRoute';
import DevProtectedRoute from './components/DevProtectedRoute';
import Landing from './pages/Landing';
import About from './pages/LandingPages/About';
import Contact from './pages/LandingPages/Contact';
import Solutions from './pages/LandingPages/Solutions';
import Terms from './pages/LandingPages/Terms';
import Privacy from './pages/LandingPages/Privacy';
import Cookies from './pages/LandingPages/Cookies';
import Register from './pages/Register';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import SetupWizard from './pages/SetupWizard';

import EmployeeDashboard from './pages/EmployeeDashboard';
import PreboardingSetup from './pages/PreboardingSetup';
import OnboardingSetup from './pages/OnboardingSetup';
import EmployeesViewAdmin from './pages/EmployeesViewAdmin';
import AdminTasksPage from './pages/AdminTasksPage';
import ActivityCentreOverview from './pages/ActivityCentreOverview';
import AdminFilesPage from './pages/AdminFilesPage';
import AdminNoticesPage from './pages/AdminNoticesPage';
import AdminCoursesPage from './pages/AdminCoursesPage';
import EmployeeTaskDashboard from './pages/EmployeeTaskDashboard';
import EmployeeTaskDetailPage from './pages/EmployeeTaskDetailPage';
import EmployeeNoticesPage from './pages/EmployeeNoticesPage';
import EmployeeFilesPage from './pages/EmployeeFilesPage';
import AppLayout from './components/AppLayout';
import AdminMessagesPage from './pages/AdminMessagesPage';
import EmployeeMessagesPage from './pages/EmployeeMessagesPage';
import AdminCompliancePage from './pages/AdminCompliancePage';
import AdminAIPage from './pages/AdminAIPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import EmployeeSettingsPage from './pages/EmployeeSettingsPage';
import EmployeeAIPage from './pages/EmployeeAIPage';
import AdminSurveysPage from './pages/AdminSurveysPage';
import EmployeeSurveysPage from './pages/EmployeeSurveysPage';
import EmployeeNotificationsPage from './pages/EmployeeNotificationsPage';
import AdminNotificationsPage from './pages/AdminNotificationsPage';
import MyActivitiesPage from './pages/MyActivitiesPage';
import EmailTemplateEditor from './pages/EmailTemplateEditor';
import DevDashboard from './pages/DevDashboard';
import ActivationPage from './pages/ActivationPage';
import ActivationSuccessPage from './pages/ActivationSuccessPage';

import './App.css';
import { CookieConsentProvider } from './context/CookieConsentContext';
import CookieConsentBanner from './components/CookieConsentBanner';

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/solutions" element={<Solutions />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />


      {/* ----------------Developer Dashboard------------------- */}
      <Route path="/dev" element={
        <DevProtectedRoute>
          <DevDashboard />
        </DevProtectedRoute>
      } />

      {/* ----------------Admin Routes------------------- */}
      {/* Admin Routes */}
      <Route
        path="/admin" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* /admin Home */}
        <Route index element={<AdminDashboard />} />
        {/* /admin/setup */}
        <Route path="setup" element={<SetupWizard />} />

        {/* /admin/employees */}
        <Route path="employees" element={<EmployeesViewAdmin />} />

        {/* ---- Activity Centre Routes ---- */}
        <Route path="activity-centre" element={<ActivityCentreOverview />} />
        <Route path="activity-centre/tasks" element={<AdminTasksPage />} />
        <Route path="activity-centre/courses" element={<AdminCoursesPage />} />
        <Route path="activity-centre/notices" element={<AdminNoticesPage />} />
        <Route path="activity-centre/compliance" element={<AdminCompliancePage />} />
        <Route path="activity-centre/surveys" element={<AdminSurveysPage />} />

        {/* Backward compatibility redirects */}
        <Route path="tasks" element={<Navigate to="/admin/activity-centre/tasks" replace />} />
        <Route path="courses" element={<Navigate to="/admin/activity-centre/courses" replace />} />
        <Route path="notices" element={<Navigate to="/admin/activity-centre/notices" replace />} />
        <Route path="compliance" element={<Navigate to="/admin/activity-centre/compliance" replace />} />
        <Route path="surveys" element={<Navigate to="/admin/activity-centre/surveys" replace />} />

        {/* /admin/files */}
        <Route path="files" element={<AdminFilesPage />} />
        {/* /admin/messages */}
        <Route path="messages" element={<AdminMessagesPage />} />
        {/* /admin/ai */}
        <Route path="ai" element={<AdminAIPage />} />
        {/* /admin/settings */}
        <Route path="settings" element={<AdminSettingsPage />} />
        <Route path="settings/templates/:id" element={<EmailTemplateEditor />} />
        {/* /admin/notifications */}
        <Route path="notifications" element={<AdminNotificationsPage />} />
        {/* /admin/my-activities */}
        <Route path="my-activities" element={<MyActivitiesPage />} />
        {/* /admin/tasks/:taskId – admin viewing their own assigned task */}
        <Route path="tasks/:taskId" element={<EmployeeTaskDetailPage />} />
        {/* /admin/my-surveys – admin taking surveys assigned to them */}
        <Route path="my-surveys" element={<EmployeeSurveysPage />} />
      </Route>



      {/* ----------------Preboarding Routes------------------- */}

      <Route path="/preboard/:token" element={<PreboardingSetup />} />
      <Route path="/onboard/:token" element={<OnboardingSetup />} />

      {/* ----------------Activation Routes (Stripe)------------------- */}
      <Route path="/activate/:token" element={<ActivationPage />} />
      <Route path="/activate/:token/success" element={<ActivationSuccessPage />} />

      <Route
        path="/preboard/:token/dashboard"
        element={
          <ProtectedRoute allowedRoles={['employee']}>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<EmployeeDashboard />} />
        <Route path="tasks" element={<EmployeeTaskDashboard />} />
        <Route path="tasks/:taskId" element={<EmployeeTaskDetailPage />} />
        <Route path="notices" element={<EmployeeNoticesPage />} />
        <Route path="messages" element={<EmployeeMessagesPage />} />
        <Route path="files" element={<EmployeeFilesPage />} />
        <Route path="surveys" element={<EmployeeSurveysPage />} />
        <Route path="ai" element={<EmployeeAIPage />} />
        <Route path="settings" element={<EmployeeSettingsPage />} />
        <Route path="notifications" element={<EmployeeNotificationsPage />} />
        <Route path="my-activities" element={<MyActivitiesPage />} />
      </Route>
      {/* --------------------------------------------------- */}


      {/* ----------------Employee Routes------------------- */}
      <Route
        path="/employee"
        element={
          <ProtectedRoute allowedRoles={['employee']}>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* /Employee Home */}
        <Route index element={<EmployeeDashboard />} />

        {/* /employee/tasks */}
        <Route path="tasks" element={<EmployeeTaskDashboard />} />
        {/* /employee/tasks/:taskId */}
        <Route path="tasks/:taskId" element={<EmployeeTaskDetailPage />} />
        {/* /employee/notices */}
        <Route path="notices" element={<EmployeeNoticesPage />} />
        {/* /employee/files */}
        <Route path="files" element={<EmployeeFilesPage />} />
        {/* /employee/messages */}
        <Route path="messages" element={<EmployeeMessagesPage />} />
        {/* /employee/surveys */}
        <Route path="surveys" element={<EmployeeSurveysPage />} />
        {/* /employee/settings */}
        <Route path="settings" element={<EmployeeSettingsPage />} />
        {/* /employee/ai */}
        <Route path="ai" element={<EmployeeAIPage />} />
        {/* /employee/notifications */}
        <Route path="notifications" element={<EmployeeNotificationsPage />} />
        {/* /employee/my-activities */}
        <Route path="my-activities" element={<MyActivitiesPage />} />
        {/* Add Other Employee Routes Here */}

      </Route>
      {/* --------------------------------------------------- */}

      {/* Redirect any unknown routes to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </>
  ),
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  }
);

function App() {
  return (
    <HelmetProvider>
      <CookieConsentProvider>
        <CookieConsentBanner />
        <ThemeProvider>
          <AuthProvider>
            <NotificationProvider>
              <AdminNotificationProvider>
                <MessageProvider>
                  <Toaster
                    position="top-right"
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: '#333',
                        color: '#fff',
                        padding: '16px',
                        borderRadius: '8px',
                      },
                      success: {
                        duration: 3000,
                        style: {
                          background: '#10B981', // Emerald 500
                        },
                        iconTheme: {
                          primary: '#fff',
                          secondary: '#10B981',
                        },
                      },
                      error: {
                        duration: 5000,
                        style: {
                          background: '#EF4444', // Red 500
                        },
                        iconTheme: {
                          primary: '#fff',
                          secondary: '#EF4444',
                        },
                      },
                      loading: {
                        duration: Infinity,
                      },
                    }}
                  >
                    {(t) => (
                      <div
                        style={{
                          opacity: t.visible ? 1 : 0,
                          background: t.type === 'success' ? '#10B981' : t.type === 'error' ? '#EF4444' : '#333',
                          color: '#fff',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          minWidth: '300px',
                          maxWidth: '400px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          transition: 'all 0.2s ease-in-out',
                          transform: t.visible ? 'translateY(0)' : 'translateY(-20px)',
                        }}
                      >
                        <div style={{ display: 'flex', items: 'center', gap: '8px' }}>
                          {t.icon}
                          <span style={{ fontSize: '14px', fontWeight: 500 }}>
                            {typeof t.message === 'function' ? t.message(t) : t.message}
                          </span>
                        </div>
                        {t.type !== 'loading' && (
                          <button
                            onClick={() => toast.dismiss(t.id)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'rgba(255, 255, 255, 0.8)',
                              cursor: 'pointer',
                              padding: '4px',
                              marginLeft: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '4px',
                              transition: 'background 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </Toaster>
                  <RouterProvider router={router} />
                </MessageProvider>
              </AdminNotificationProvider>
            </NotificationProvider>
          </AuthProvider>
        </ThemeProvider>
      </CookieConsentProvider>
    </HelmetProvider>
  );
}

export default App;
